/**
 * SegmentPicker
 *
 * Selector de segmentos del paso 1 del wizard manual. Ofrece dos modos:
 *
 *   1. Elegir uno existente — lista los segmentos guardados de la marca.
 *   2. Crear uno nuevo aquí — abre InlineSegmentCreator (modal con IA).
 *
 * El cambio de modo es controlado con un toggle prominente arriba.
 * Después de crear uno nuevo, queda seleccionado automáticamente y vuelve
 * al modo "elegir" para mostrar la lista actualizada.
 */

import { useEffect, useMemo, useState } from "react";
import {
  Users,
  Search,
  Check,
  Loader2,
  AlertCircle,
  Plus,
  Sparkles,
  Pencil,
} from "lucide-react";
import { segmentsApi } from "../../segments/api";
import type { Segment } from "../../segments/types";
import { InlineSegmentCreator } from "./InlineSegmentCreator";

interface SegmentPickerProps {
  brandId: string;
  selectedId?: string;
  onSelect: (segment: Segment) => void;
}

type Mode = "pick" | "create";

export function SegmentPicker({
  brandId,
  selectedId,
  onSelect,
}: SegmentPickerProps) {
  const [mode, setMode] = useState<Mode>("pick");
  const [segments, setSegments] = useState<Segment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [creatorOpen, setCreatorOpen] = useState(false);

  /* ─── Load list ─── */
  const loadSegments = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await segmentsApi.list(brandId, "saved");
      if (res.success) setSegments(res.data);
      else setError(res.error || "Error al cargar segmentos");
    } catch (err: any) {
      setError(err.message || "Error inesperado");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSegments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brandId]);

  /* ─── Filter ─── */
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return segments;
    return segments.filter((s) => (s.name || "").toLowerCase().includes(q));
  }, [segments, search]);

  /* ─── Created callback ─── */
  const handleCreated = async (segment: Segment) => {
    setCreatorOpen(false);
    await loadSegments();
    onSelect(segment);
    setMode("pick");
  };

  return (
    <>
      {/* Mode toggle */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
        <ModeCard
          icon={Users}
          label="Elegir uno existente"
          description={`${segments.length} segmento${segments.length === 1 ? "" : "s"} guardado${segments.length === 1 ? "" : "s"}`}
          active={mode === "pick"}
          accent="emerald"
          onClick={() => setMode("pick")}
        />
        <ModeCard
          icon={Sparkles}
          label="Crear uno nuevo"
          description="Describí tu audiencia y la IA arma el filtro"
          active={mode === "create"}
          accent="violet"
          onClick={() => {
            setMode("create");
            setCreatorOpen(true);
          }}
        />
      </div>

      {/* PICK MODE: search + list */}
      {mode === "pick" && (
        <>
          {loading ? (
            <div className="flex flex-col items-center justify-center py-12 bg-slate-50 rounded-2xl">
              <Loader2 className="animate-spin text-violet-500 mb-3" size={28} />
              <p className="text-sm text-slate-500">Cargando segmentos…</p>
            </div>
          ) : error ? (
            <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-100 rounded-xl text-red-700">
              <AlertCircle size={18} />
              <span className="text-sm font-medium">{error}</span>
            </div>
          ) : segments.length === 0 ? (
            <EmptyState onCreate={() => setCreatorOpen(true)} />
          ) : (
            <div className="space-y-3">
              {/* Search */}
              <div className="relative">
                <Search
                  size={15}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
                />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar segmento…"
                  className="w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-xl text-sm
                    focus:outline-none focus:ring-2 focus:ring-violet-200 focus:border-violet-300 transition-all"
                />
              </div>

              {/* List */}
              <div className="space-y-2 max-h-[400px] overflow-y-auto pr-1">
                {filtered.length === 0 ? (
                  <div className="text-sm text-slate-400 py-4 text-center">
                    Ningún segmento coincide con "{search}"
                  </div>
                ) : (
                  filtered.map((segment) => {
                    const isSelected = selectedId === segment.id;
                    return (
                      <button
                        key={segment.id}
                        onClick={() => onSelect(segment)}
                        className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                          isSelected
                            ? "border-violet-300 bg-violet-50"
                            : "border-slate-100 bg-white hover:border-slate-200"
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          <div
                            className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                              isSelected ? "bg-violet-100" : "bg-slate-100"
                            }`}
                          >
                            <Users
                              size={15}
                              className={
                                isSelected
                                  ? "text-violet-600"
                                  : "text-slate-500"
                              }
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="font-semibold text-slate-900 truncate">
                                {segment.name || "Sin nombre"}
                              </p>
                              {isSelected && (
                                <Check
                                  size={14}
                                  className="text-violet-600 flex-shrink-0"
                                />
                              )}
                            </div>
                            <div className="flex items-center gap-3 text-xs text-slate-500">
                              <span className="font-mono">
                                {(segment.lastKnownCount || 0).toLocaleString()}{" "}
                                usuarios
                              </span>
                              {segment.conversationSummary && (
                                <>
                                  <span className="text-slate-300">·</span>
                                  <span className="truncate italic">
                                    {segment.conversationSummary}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                      </button>
                    );
                  })
                )}
              </div>

              {/* Inline "create another" link */}
              <button
                type="button"
                onClick={() => setCreatorOpen(true)}
                className="w-full mt-2 py-3 rounded-xl border-2 border-dashed border-slate-200 hover:border-violet-300 hover:bg-violet-50/30 text-slate-500 hover:text-violet-700 text-sm font-medium transition-all flex items-center justify-center gap-2"
              >
                <Plus size={14} />
                Crear un segmento nuevo
              </button>
            </div>
          )}
        </>
      )}

      {/* CREATE MODE: launches modal on enter, when closed without creating we stay here */}
      {mode === "create" && !creatorOpen && (
        <div className="bg-violet-50/40 border border-violet-100 rounded-2xl p-6 text-center">
          <Sparkles size={28} className="text-violet-500 mx-auto mb-3" />
          <p className="text-sm font-semibold text-slate-900 mb-1">
            ¿Cerraste el creador?
          </p>
          <p className="text-xs text-slate-500 mb-4">
            Podés volver a abrirlo o cambiar a la lista de segmentos existentes.
          </p>
          <div className="flex gap-2 justify-center">
            <button
              type="button"
              onClick={() => setCreatorOpen(true)}
              className="px-4 py-2 rounded-lg bg-violet-600 text-white text-sm font-semibold hover:bg-violet-700 transition-colors flex items-center gap-1.5"
            >
              <Pencil size={13} />
              Abrir creador
            </button>
            <button
              type="button"
              onClick={() => setMode("pick")}
              className="px-4 py-2 rounded-lg bg-white border border-slate-200 text-slate-700 text-sm font-semibold hover:bg-slate-50 transition-colors"
            >
              Ver existentes
            </button>
          </div>
        </div>
      )}

      {/* Modal */}
      <InlineSegmentCreator
        open={creatorOpen}
        brandId={brandId}
        onClose={() => setCreatorOpen(false)}
        onCreated={handleCreated}
      />
    </>
  );
}

/* ─── Mode card ─── */
interface ModeCardProps {
  icon: typeof Users;
  label: string;
  description: string;
  active: boolean;
  accent: "emerald" | "violet";
  onClick: () => void;
}

function ModeCard({
  icon: Icon,
  label,
  description,
  active,
  accent,
  onClick,
}: ModeCardProps) {
  const activeClasses =
    accent === "emerald"
      ? "border-emerald-300 bg-emerald-50"
      : "border-violet-300 bg-violet-50";
  const iconBg =
    accent === "emerald"
      ? active
        ? "bg-emerald-100 text-emerald-700"
        : "bg-slate-100 text-slate-500"
      : active
        ? "bg-violet-100 text-violet-700"
        : "bg-slate-100 text-slate-500";

  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-left p-4 rounded-xl border-2 transition-all ${
        active ? activeClasses : "border-slate-100 hover:border-slate-200"
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${iconBg}`}
        >
          <Icon size={17} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-slate-900 text-sm">{label}</p>
          <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">
            {description}
          </p>
        </div>
      </div>
    </button>
  );
}

/* ─── Empty state ─── */
function EmptyState({ onCreate }: { onCreate: () => void }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-100 p-8 text-center">
      <div className="w-14 h-14 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
        <Users size={26} className="text-slate-400" />
      </div>
      <p className="text-slate-700 font-medium mb-1">
        Todavía no tenés segmentos guardados
      </p>
      <p className="text-slate-500 text-sm mb-4 max-w-md mx-auto">
        Creá tu primero acá mismo en 30 segundos describiendo a tu audiencia
        en lenguaje natural.
      </p>
      <button
        type="button"
        onClick={onCreate}
        className="inline-flex items-center gap-1.5 px-4 py-2 bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white text-sm font-semibold rounded-lg hover:from-violet-700 hover:to-fuchsia-700 transition-all shadow-sm shadow-violet-500/25"
      >
        <Sparkles size={14} />
        Crear mi primer segmento
      </button>
    </div>
  );
}

export default SegmentPicker;
